import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import ModalSection from '../ModalSection';
import RetailerModel from '../../../../../components/Retailers/RetailerModel';
import PlatformModel from '../../../../models/PlatformModel';
import PlatformRetailersModelMC from '../../../../models/PlatformRetailersModelMC';
import AgentModel from '../../../../models/AgentModel';
import PlatformsOptions from './PlatformsOptions';
import { buildAccountKey, flattenAllChatAccounts } from './PlatformAgentAccounts';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import { getRetailerInfo } from '../../../../../actions/retailerUsersActions';
import ToggleSwitchMC from '../../../../components/ToggleSwitchMC';
import UpgradePlanModal from './UpgradePlanModal';
import { addToDataLayer } from '../../../../../util/gtm';
import EmptyScreenInboxMC from '../../../../components/EmptyScreenInboxMC';
import TagModel from '../../../../models/TagsModelMC';
import SearchableMultiSelectMC from '../../../../components/SearchableMultiSelectMC';
import InputComponent from '../../../../components/InputMC';
import FormProviderMC from '../../../../components/FormProviderMC';
import { setMiaAgentMsInbox } from '../../../../../services/inbox/msInboxSetMiaAgentClient';
import MiaRetailerModel from '../../../../models/MiaRetailerModel';
import showtoast from '../../../../../util/toast';
import TestCustomersList from './TestCustomersList';
import {
  extractCustomersToTestAi,
  fetchCustomerOption,
  mapWithConcurrency
} from '../utils/testCustomers';

const MIA_WAIT_TIME_MIN = 5;
const MIA_WAIT_TIME_MAX = 60;

const PlatformsModal = ({ focusAdditionalChats = false }) => {
  const dispatch = useDispatch();
  const [highlightChats, setHighlightChats] = useState(false);

  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const { t } = useTranslation('ChatbotIA');

  const [saving, setSaving] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [platformError, setPlatformError] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(retailer_info.mia_chatbot_active || false);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [consumeBalance, setConsumeBalance] = useState(retailer_info.mia_consume_balance ?? false);
  const [waitTime, setWaitTime] = useState(retailer_info.mia_wait_time ?? '');
  const [integrationsStatus, setIntegrationsStatus] = useState(null);
  const [agents, setAgents] = useState(null);
  const [pendingAgentValues, setPendingAgentValues] = useState({});
  const [testCustomers, setTestCustomers] = useState([]);

  const waitTimeFormMethods = useForm();

  const combinedRetailerInfo = useMemo(
    () => ({
      ...retailer_info,
      ...integrationsStatus,
      tiktok_integrated: integrationsStatus?.tik_tok_messages_integrated === true
    }),
    [retailer_info, integrationsStatus]
  );

  useEffect(() => {
    setChatbotEnabled(retailer_info.mia_chatbot_active);
  }, [
    retailer_info.mia_chatbot_active
  ]);

  const additionalChatsRef = useCallback((node) => {
    if (!focusAdditionalChats || !node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightChats(true);
  }, [focusAdditionalChats]);

  useEffect(() => {
    if (!highlightChats) return undefined;
    const timer = setTimeout(() => setHighlightChats(false), 2500);
    return () => clearTimeout(timer);
  }, [highlightChats]);

  useEffect(() => {
    if (retailer_info.retailer_mia_platforms?.length > 0) {
      const relatedPlatforms = retailer_info.retailer_mia_platforms.map((platform) => platform.mia_platform_id);
      setSelectedPlatforms(relatedPlatforms);
    }

    if (retailer_info.retailer_mia_disabled_tags?.length > 0) {
      setSelectedTags(retailer_info.retailer_mia_disabled_tags.map((mia_tag) => ({ value: mia_tag.tag_id })));
    }
    setConsumeBalance(retailer_info.mia_consume_balance ?? false);
    setWaitTime(retailer_info.mia_wait_time ?? '');
  }, [
    retailer_info.retailer_mia_platforms,
    retailer_info.retailer_mia_disabled_tags,
    retailer_info.mia_consume_balance,
    retailer_info.mia_wait_time
  ]);

  useEffect(() => {
    loadPlatforms();
    loadTags();
    loadIntegrationsStatus();
    loadAgents();
    loadTestCustomers();
  }, []);

  const loadIntegrationsStatus = () => {
    PlatformRetailersModelMC.getStatus()
      .then((statusResponse) => setIntegrationsStatus(statusResponse))
      .catch(() => setIntegrationsStatus(null));
  };

  const loadAgents = () => {
    AgentModel.get({ per_page: 100 })
      .then((res) => setAgents(res?.data || []))
      .catch(() => setAgents([]));
  };

  const agentsLoading = integrationsStatus === null || agents === null;

  const agentOptions = useMemo(() => [
    { value: '', label: t('config.platforms.agent_accounts.default_agent') },
    ...(agents || []).map((agent) => ({ value: agent.id, label: agent.name }))
  ], [agents, t]);

  // `AsyncPaginatedSelectMC` resuelve el label de `defaultValue` en un efecto que solo
  // corre una vez por instancia: si esta closure cambiara de identidad después del mount
  // (p. ej. por depender de `agents` antes de cargar), el select nunca vería la lista
  // completa. Por eso las filas no se montan hasta que `agentsLoading` es false.
  const loadAgentOptions = async () => ({ options: agentOptions, hasMore: false });

  const handleAgentSelect = (accountKey, value) => {
    setPendingAgentValues((prev) => ({ ...prev, [accountKey]: value }));
  };

  const isAgentDeleted = (account) => (
    account.mia_agent_id
      && agents
      && !agents.some((agent) => String(agent.id) === String(account.mia_agent_id))
  );

  // Secuencial, no Promise.all: evita saturar el MS con N PUTs simultáneos. Solo manda
  // las cuentas cuyo select el usuario tocó (`buildAccountKey(...) in pendingAgentValues`);
  // comparar contra `account.mia_agent_id` fallaría siempre, porque integrations-status
  // no lo devuelve hoy.
  const saveAgentAssignments = async () => {
    const allAccounts = flattenAllChatAccounts(integrationsStatus, combinedRetailerInfo);
    const changed = allAccounts.filter((account) => buildAccountKey(account.platform, account) in pendingAgentValues);

    if (changed.length === 0) return false;

    const failedAccounts = await changed.reduce(async (accPromise, account) => {
      const acc = await accPromise;
      const key = buildAccountKey(account.platform, account);
      const value = pendingAgentValues[key];

      return setMiaAgentMsInbox(account.id, value === '' ? null : value)
        .then(() => acc)
        .catch(() => [...acc, account.name || account.phone_number || account.id]);
    }, Promise.resolve([]));

    return failedAccounts.length > 0;
  };

  const loadTestCustomers = () => {
    MiaRetailerModel.get()
      .then(async (response) => {
        const ids = extractCustomersToTestAi(response);
        const options = await mapWithConcurrency(ids, (id) => fetchCustomerOption(id).catch(() => ({
          id,
          label: String(id)
        })));
        setTestCustomers(options.filter(Boolean));
      })
      .catch(() => {
        setTestCustomers([]);
      });
  };

  const refreshRetailerInfo = () => {
    dispatch(getRetailerInfo({ force: true }));
  };

  const togglePlatformsModal = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const loadTags = () => {
    TagModel.get()
      .then((res) => {
        setTagOptions(res.tags);
      })
      .catch(() => { });
  };

  const loadPlatforms = () => {
    setLoadingPlatforms(true);
    PlatformModel.get()
      .then((res) => {
        const { platforms } = res;

        const combinedOptions = platforms.map((platform) => ({
          key: platform.id,
          label: platform.name,
          description: platform.name,
          icon: platform.svg_content || '',
          hasCheckbox: platform.active,
          enabled: platform.active,
          checked: platform.active
        }));
        setLoadingPlatforms(false);
        setPlatformOptions(combinedOptions);
      })
      .catch((err) => {
        setLoadingPlatforms(false);
        showtoast(err.error || t('config.error_loading'));
      });
  };

  const onSubmit = () => {
    if (!retailer_info.chatbot_ai && retailer_info.in_testing_mia !== true) {
      setIsUpgradeModalOpen(true);
      return;
    }

    if (chatbotEnabled && selectedPlatforms.length === 0) {
      setPlatformError(true);
      return;
    }

    setPlatformError(false);
    setSaving(true);

    let params = {
      mia_chatbot_active: chatbotEnabled,
      mia_consume_balance: consumeBalance,
      mia_wait_time: waitTime === '' ? null : Number(waitTime)
    };

    if (chatbotEnabled) {
      const platformsToUpdate = retailer_info.retailer_mia_platforms.map((platform) => {
        const isSelected = selectedPlatforms.includes(platform.mia_platform_id);
        return {
          id: platform.id,
          _destroy: !isSelected
        };
      });

      const platformsToAdd = selectedPlatforms
        .filter((platformId) => !retailer_info.retailer_mia_platforms.some((p) => p.mia_platform_id === platformId))
        .map((platformId) => ({ mia_platform_id: platformId }));

      params['retailer_mia_platforms_attributes'] = platformsToUpdate.concat(platformsToAdd);
    }

    const selectedTagIds = selectedTags.map((tag) => tag.value);

    const tagsToUpdate = (retailer_info.retailer_mia_disabled_tags || []).map((mia_tag) => {
      const isSelected = selectedTagIds.includes(mia_tag.tag_id);
      return {
        id: mia_tag.id,
        _destroy: !isSelected
      };
    });

    const tagsToAdd = selectedTagIds
      .filter((tagId) => !(retailer_info.retailer_mia_disabled_tags || []).some((mia_tag) => mia_tag.tag_id === tagId))
      .map((tagId) => ({ tag_id: tagId }));

    params['retailer_mia_disabled_tags_attributes'] = tagsToUpdate.concat(tagsToAdd);

    RetailerModel.toggleOrAddMiaPlatform({ retailer: params })
      .then(async () => {
        if (chatbotEnabled) {
          addToDataLayer({
            event: 'mia_activated',
            medium_integration: selectedPlatforms.map((p) => platformOptions.find((o) => o.key === p)?.label)
          });
        }

        const agentSaveFailed = await saveAgentAssignments();

        setTimeout(() => {
          showtoast(agentSaveFailed
            ? t('config.platforms.agent_accounts.save_error')
            : t('config.save_success'));
          setSaving(false);
          togglePlatformsModal(null);
          refreshRetailerInfo();
        }, 1000);
      })
      .catch((error) => {
        setSaving(false);
        showtoast(error.message || error.error || t('config.save_error'));
      });
  };

  const handlePlatformsChange = (platformId) => {
    setPlatformError(false);

    setSelectedPlatforms((prevSelected) => {
      if (prevSelected.includes(platformId)) {
        return prevSelected.filter((id) => id !== platformId);
      }

      return [...prevSelected, platformId];
    });
  };

  const toggleChatbot = () => {
    const newValue = !chatbotEnabled;

    setChatbotEnabled(newValue);
    if (!newValue) {
      setSelectedPlatforms([]);
      setPlatformError(false);
    }
  };

  return (
    <div className="tw-wrapper">
      {retailer_info.whatsapp_integrated || retailer_info.messenger_integrated || retailer_info.instagram_integrated ? (
        <>
          <RightModalLayout
            title={t('config.platforms.modal_title')}
            submitAction={onSubmit}
            submitLabel={t('guides.sidebar_create.update')}
            submitDisabled={saving}
          >
            <div className="tw-flex tw-flex-col tw-gap-6">
              {/* Sección 1: Conexión */}
              <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-flex-col">
                  <ToggleSwitchMC
                    isOn={chatbotEnabled}
                    handleToggle={toggleChatbot}
                    label={t('config.platforms.section_1_title')}
                    labelClassName="!tw-font-semibold"
                  />
                  <span className="tw-text-gray-950 tw-text-xs">{t('config.platforms.section_1_description')}</span>
                </div>

                {chatbotEnabled && (
                  <ModalSection
                    title={t('config.platforms.section_2_title')}
                    description={t('config.platforms.section_2_description')}
                    afterContent={(
                      <>
                        <PlatformsOptions
                          selectedPlatforms={selectedPlatforms}
                          selectPlatforms={handlePlatformsChange}
                          options={platformOptions}
                          loadingPlatforms={loadingPlatforms}
                          retailerInfo={combinedRetailerInfo}
                          integrationsStatus={integrationsStatus}
                          agentsLoading={agentsLoading}
                          loadOptions={loadAgentOptions}
                          pendingValues={pendingAgentValues}
                          onAgentSelect={handleAgentSelect}
                          isAgentDeleted={isAgentDeleted}
                        />
                        {platformError && (
                          <p className="error-text text-danger">
                            {t('config.platforms.error_no_platform')}
                          </p>
                        )}
                      </>
                    )}
                  />
                )}
              </div>

              {/* Sección 2: Exclusiones + Chats adicionales (independiente del toggle) */}
              <div className="tw-flex tw-flex-col tw-gap-4 tw-pt-4 tw-border-t tw-border-gray-200">
                <ModalSection
                  title={t('config.platforms.wait_time_title')}
                  description={t('config.platforms.wait_time_description')}
                  afterContent={(
                    <div className="tw-mt-2 tw-w-1/2">
                      <FormProviderMC methods={waitTimeFormMethods} onSubmit={(e) => e.preventDefault()}>
                        <InputComponent
                          id="mia_wait_time"
                          name="mia_wait_time"
                          label={t('config.platforms.wait_time_label')}
                          useFloatingLabel
                          integer
                          value={waitTime}
                          onChange={(_, value) => setWaitTime(value)}
                        />
                      </FormProviderMC>
                      <span className="tw-font-regular tw-text-xs tw-text-gray-500 tw-block tw-mt-1">
                        {t('config.platforms.wait_time_hint', {
                          min: MIA_WAIT_TIME_MIN,
                          max: MIA_WAIT_TIME_MAX
                        })}
                      </span>
                    </div>
                  )}
                />

                <ModalSection
                  title={t('config.platforms.section_3_title')}
                  description={t('config.platforms.section_3_description')}
                  afterContent={(
                    <>
                      <div className="tw-bg-blue-50 tw-border tw-border-blue-100 tw-flex tw-my-4 tw-px-2 tw-py-1 tw-rounded-xl">
                        <span className="tw-flex tw-items-center tw-gap-1 tw-font-medium tw-text-[10px] tw-text-blue-500 tw-leading-none">
                          <i className="mi-info-outline tw-text-[16px] tw-mt-1"></i>
                          {t('config.platforms.label_info')}
                        </span>
                      </div>

                      <SearchableMultiSelectMC
                        options={tagOptions}
                        selected={selectedTags}
                        onChange={(selectedOptions) => setSelectedTags(selectedOptions || [])}
                        onClearAll={() => setSelectedTags([])}
                      />
                    </>
                  )}
                />

                <div
                  ref={additionalChatsRef}
                  className={highlightChats ? 'tw-rounded-lg tw-ring-2 tw-ring-yellow-400' : undefined}
                >
                  <ModalSection
                    title={t('config.additional_chats_title')}
                    description={t('config.additional_chats_description')}
                    afterContent={(
                    <div
                      className={`tw-flex tw-items-start tw-gap-3 tw-mt-2 tw-p-3 tw-rounded-lg tw-border tw-cursor-pointer ${
                        consumeBalance
                          ? 'tw-border-blue-400 tw-bg-blue-50'
                          : 'tw-border-gray-200 tw-bg-white'
                      }`}
                      onClick={() => {
                        setConsumeBalance(!consumeBalance);
                        setHighlightChats(false);
                      }}
                    >
                      <div className="tw-flex-1">
                        <span className="tw-font-semibold tw-text-sm tw-text-gray-800 tw-block">
                          {t('config.consume_balance_label')}
                        </span>
                        <span className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-block">
                          {consumeBalance
                            ? t('config.consume_balance_description')
                            : t('config.consume_balance_off_note')}
                        </span>
                      </div>
                      <div className="tw-flex-shrink-0 tw-self-center">
                        <div className={`tw-w-5 tw-h-5 tw-rounded tw-flex tw-items-center tw-justify-center ${
                          consumeBalance
                            ? 'tw-bg-blue-500'
                            : 'tw-border-2 tw-border-gray-300 tw-bg-white'
                        }`}>
                          {consumeBalance && (
                            <svg className="tw-w-3 tw-h-3 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                    )}
                  />
                </div>

                <ModalSection
                  title={t('config.test_customers.title')}
                  description={t('config.test_customers.description')}
                  afterContent={(
                    <div className="tw-mt-2">
                      <TestCustomersList customers={testCustomers} />
                    </div>
                  )}
                />
              </div>
            </div>
          </RightModalLayout>

          <UpgradePlanModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
        </>
      ) : (
        <>
          <RightModalLayout
            title={t('config.platforms.modal_title')}
          >
            <EmptyScreenInboxMC platform="whatsapp_modal" customTitle="inbox.title2" />
          </RightModalLayout>
        </>
      )}
    </div>
  );
};

export default PlatformsModal;
